import { Product } from '../types';
import { Locale } from '../i18n/locales';
import { SolutionsKm } from './solutions.km';

export const Solutions: Product[] = [
   {
      id: 1,
      slug: 'sales',
      name: 'Sales Solutions',
      shortName: 'Sales',
      tagline: 'Lead, quote, checkout, and collect in one chain',
      docsUrl: 'https://docs.akas.co/en/crm/',
      description:
         'Sell everywhere with connected POS, ecommerce, and CRM. Create quotes, convert to invoices, and track revenue from lead to cash with AKAS.',
      images: [],
      backgroundImage: '/images/backgrounds/crm.jpeg',
      mainFeatures: [
         {
            icon: '🧾',
            title: 'Quotations & Invoicing',
            description:
               'Build itemized quotations with taxes, discounts, and payment terms; convert to invoices in one click and collect payments faster.',
            images: [],
         },
         {
            icon: '🛒',
            title: 'Point of Sale (POS)',
            description:
               'Speed up checkout with barcode scanning, printable/emailed receipts, hold and resume carts, cash drawer reconciliation, and offline-first resilience.',
            images: [],
         },
         {
            icon: '🏪',
            title: 'Ecommerce & Catalog',
            description:
               'Publish product catalogs with pricing tiers, manage stock availability, and sync orders from web, marketplace, and in-store channels.',
            images: [],
         },
         {
            icon: '🔄',
            title: 'Sales Pipeline & CRM',
            description:
               'Capture leads from web forms, track opportunities, schedule follow-ups, and trigger automated handoffs to billing and fulfillment.',
            images: [],
         },
         {
            icon: '📦',
            title: 'Inventory & Fulfillment',
            description:
               'Track stock by location, reserve inventory on confirmed orders, and manage picking, packing, and shipping updates in one place.',
            images: [],
         },
         {
            icon: '📈',
            title: 'Sales Reporting',
            description:
               'Monitor revenue, margins, taxes, and channel performance with dashboards that mirror the KPIs documented in AKAS sales reports.',
            images: [],
         },
      ],
      meta: {
         title: 'AKAS | Sales solutions — quotes, POS, ecommerce and CRM',
         description:
            'Run quotations, invoices, POS, ecommerce, and CRM in one AKAS sales platform. Sell in-store and online with centralized products, inventory, and reporting.',
         keywords:
            'Sales Solution, POS, Ecommerce, CRM, Quotations, Invoicing, AKAS',
      },
      additionalFeatures: [
         {
            icon: '✓',
            title: 'Approval workflow',
            description:
               'Route discounts, credit notes, and large deals for approval before they post.',
         },
         {
            icon: '🌐',
            title: 'Multi-language & currency',
            description:
               'English and Khmer out of the box, with more languages, multi-currency pricing, and tax rules.',
         },
         {
            icon: '⚡',
            title: 'Offline-capable POS',
            description:
               'Keep selling during connectivity drops and sync when back online.',
         },
         {
            icon: '🔌',
            title: 'Integrations',
            description:
               'Connect payment gateways, barcode scanners, printers, and AKAS add-ons.',
         },
         {
            icon: '🔔',
            title: 'Notifications',
            description:
               'Alert sales, finance, and operations teams on key order and payment events.',
         },
         {
            icon: '📝',
            title: 'Audit logging',
            description:
               'Track user actions across quotations, invoices, and POS sessions.',
         },
      ],
      benefits: [
         {
            number: '01',
            title: 'Faster quote-to-cash',
            description:
               'Convert leads to invoices quickly with guided approvals and automated handoffs.',
         },
         {
            number: '02',
            title: 'Omnichannel control',
            description:
               'Keep POS, ecommerce, and B2B orders in sync with one product and inventory source.',
         },
         {
            number: '03',
            title: 'Reduced errors',
            description:
               'Standardize pricing, taxes, and stock reservations to cut rework and shrinkage.',
         },
         {
            number: '04',
            title: 'Better customer experience',
            description:
               'Give buyers consistent pricing, faster checkout, and clear order updates.',
         },
      ],
      videos: [],
      workflow: {
         title: 'Lead to cash',
         path: 'Lead → Customer → Quote → Sales order → Invoice → Receipt / POS / Store',
         steps: [
            {
               title: 'Win the deal',
               description: 'Capture leads, convert to customers, manage the pipeline, and send an approved quotation.',
            },
            {
               title: 'Confirm and bill',
               description: 'Turn the quote into a sales order, then invoice with the same items, taxes, and terms.',
            },
            {
               title: 'Sell in person or online',
               description: 'Mart and restaurant POS plus the online store use the same catalog and stock.',
            },
            {
               title: 'Collect and report',
               description: 'Receipts, KHQR, deposits, and AR query sit in Revenues and can post to Accounting.',
            },
         ],
      },
      highlights: [
         'Quotations convert to sales orders and invoices',
         'POS Mart and restaurant modes on the same catalog',
         'Online store, CRM pipeline, and revenue reports',
      ],
      related: [
         { label: 'CRM', to: '/products/crm' },
         { label: 'Revenues', to: '/products/revenues' },
         { label: 'POS', to: '/products/pos' },
         { label: 'Online store', to: '/products/online-store' },
      ],
   },
   {
      id: 2,
      slug: 'inventory',
      name: 'Inventory Solutions',
      shortName: 'Inventory',
      tagline: 'Order, receive, move, and balance stock across locations',
      docsUrl: 'https://docs.akas.co/en/inventory/',
      description:
         'Control stock across warehouses, keep counts accurate, and replenish on time with AKAS inventory.',
      images: [],
      backgroundImage: '/images/solutions/inventory-solution.jpg',
      mainFeatures: [
         {
            icon: '📦',
            title: 'Multi-location inventory',
            description:
               'Track on-hand, reserved, and available-to-promise by warehouse, store, or bin.',
            images: [],
         },
         {
            icon: '🔄',
            title: 'Stock movements',
            description:
               'Receive, transfer, and adjust stock with approval trails and variance notes.',
            images: [],
         },
         {
            icon: '🧾',
            title: 'Purchasing & replenishment',
            description:
               'Raise POs from reorder points and lead times; match receipts to supplier bills.',
            images: [],
         },
         {
            icon: '🚚',
            title: 'Fulfillment',
            description:
               'Pick, pack, and ship against sales orders with status updates to sales and finance.',
            images: [],
         },
         {
            icon: '🏷️',
            title: 'Lot / serial tracking',
            description:
               'Capture batch/serial numbers and expirations for traceability and recalls.',
            images: [],
         },
         {
            icon: '📈',
            title: 'Inventory analytics',
            description:
               'Monitor turnover, aging, valuation, and shrinkage with dashboards aligned to AKAS reports.',
            images: [],
         },
      ],
      meta: {
         title: 'AKAS | Inventory solutions — stock, purchasing and warehouses',
         description:
            'Control stock across warehouses, keep counts accurate, and replenish on time with AKAS inventory.',
         keywords:
            'Inventory Solution, Stock Control, Warehouse, Replenishment, AKAS',
      },
      additionalFeatures: [
         {
            icon: '✅',
            title: 'Cycle counts & audits',
            description:
               'Run scheduled counts and reconcile variances with approvals.',
         },
         {
            icon: '📡',
            title: 'Barcode & scanners',
            description:
               'Speed receiving and picking with barcode-friendly workflows.',
         },
         {
            icon: '🔔',
            title: 'Alerts',
            description:
               'Get notified on low stock, backorders, and delayed receipts.',
         },
         {
            icon: '🔌',
            title: 'Integrations',
            description:
               'Connect POS, ecommerce, procurement, and accounting modules.',
         },
         {
            icon: '📝',
            title: 'Audit logging',
            description: 'Capture who adjusted stock, when, and why.',
         },
      ],
      benefits: [
         {
            number: '01',
            title: 'Inventory accuracy',
            description:
               'Reduce stockouts and overstock with real-time, multi-location visibility.',
         },
         {
            number: '02',
            title: 'Faster fulfillment',
            description:
               'Pick, pack, and ship faster with guided workflows tied to sales orders.',
         },
         {
            number: '03',
            title: 'Lower carrying costs',
            description:
               'Replenish based on demand and lead times to cut excess stock.',
         },
         {
            number: '04',
            title: 'Compliance & traceability',
            description:
               'Lot/serial control and audit trails simplify recalls and inspections.',
         },
      ],
      videos: [],
      workflow: {
         title: 'Buy, hold, sell',
         path: 'Purchase order → Receive → Transfer / adjust / withdraw → Invoice deducts stock',
         steps: [
            {
               title: 'Order and receive',
               description: 'Raise purchase orders against vendors, then receive goods into a location.',
            },
            {
               title: 'Move stock',
               description: 'Transfer between warehouses, adjust after counts, or fulfill self-service withdrawals.',
            },
            {
               title: 'Sell',
               description: 'Invoicing inventory items in Revenues deducts on-hand quantity.',
            },
            {
               title: 'Verify',
               description: 'Balances and inventory reports show what is still on the shelf.',
            },
         ],
      },
      highlights: [
         'Purchase orders and goods received',
         'Transfers, adjustments, and self-service withdrawals',
         'Balances update when inventory items are invoiced',
      ],
      related: [
         { label: 'Inventory module', to: '/products/inventory' },
         { label: 'Expenses', to: '/products/expenses' },
         { label: 'Inventory user guide', href: 'https://docs.akas.co/en/inventory/' },
      ],
   },
];

/** Overlay the Khmer records onto the English list, falling back per slug. */
function localizeSolutions(locale?: Locale): Product[] {
   if (locale !== 'km') return Solutions;
   const kmBySlug = new Map(SolutionsKm.map((solution) => [solution.slug, solution]));
   return Solutions.map((solution) => kmBySlug.get(solution.slug) ?? solution);
}

export function getSolutionBySlug(slug?: string, locale?: Locale): Product | undefined {
   if (!slug) return undefined;
   return localizeSolutions(locale).find((solution) => solution.slug === slug);
}
